// src/lib/gamification.ts — XP, level, badge, streak logic

export const LEVEL_THRESHOLDS = [0, 500, 1500, 3500, 7000, 12000, 20000];

export const LEVEL_NAMES = [
  'Widget Trainee',
  'Layout Apprentice',
  'State Wrangler',
  'Async Artisan',
  'BLoC Engineer',
  'Render Architect',
  'dartdosage Legend',
];

export const XP_REWARDS = {
  difficulty: { 1: 10, 2: 20, 3: 30, 4: 50, 5: 80 } as Record<number, number>,
  quizCorrect: 15,
  quizPerfectBonus: 100,
  firstInCategory: 25,
  categoryComplete: 200,
  dailyLogin: 50,
  streakBonus3: 30,
  streakBonus7: 75,
  streakBonus30: 200,
} as const;

export function calculateLevel(xp: number): number {
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_THRESHOLDS[i]) return i + 1;
  }
  return 1;
}

export function getLevelName(level: number): string {
  return LEVEL_NAMES[Math.min(level - 1, LEVEL_NAMES.length - 1)];
}

export function getProgressToNextLevel(xp: number): number {
  const level = calculateLevel(xp);
  const currentThreshold = LEVEL_THRESHOLDS[level - 1] ?? 0;
  const nextThreshold = LEVEL_THRESHOLDS[level] ?? LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
  if (nextThreshold === currentThreshold) return 100;
  return Math.round(((xp - currentThreshold) / (nextThreshold - currentThreshold)) * 100);
}

export function getStreakMultiplier(streak: number): number {
  if (streak >= 30) return 2.5;
  if (streak >= 7) return 2.0;
  if (streak >= 3) return 1.5;
  return 1.0;
}

export function getXPWithStreak(baseXP: number, streak: number): number {
  return Math.round(baseXP * getStreakMultiplier(streak));
}

export function getDifficultyColor(level: number): string {
  const colors: Record<number, string> = {
    1: '#22C55E',
    2: '#84CC16',
    3: '#F59E0B',
    4: '#EF4444',
    5: '#8B5CF6',
  };
  return colors[level] ?? '#6B7280';
}

export function getDifficultyLabel(level: number): string {
  const labels: Record<number, string> = {
    1: 'Beginner',
    2: 'Easy',
    3: 'Intermediate',
    4: 'Advanced',
    5: 'Expert',
  };
  return labels[level] ?? 'Unknown';
}

export function getXPForDifficulty(difficulty: number): number {
  return XP_REWARDS.difficulty[difficulty] ?? 10;
}

export function formatXP(xp: number): string {
  if (xp >= 1000) return `${(xp / 1000).toFixed(1)}k`;
  return xp.toString();
}

export function getBadgeRarityColor(rarity: string): string {
  switch (rarity) {
    case 'common':
      return '#9CA3AF';
    case 'rare':
      return '#3B82F6';
    case 'epic':
      return '#8B5CF6';
    case 'legendary':
      return '#F59E0B';
    default:
      return '#9CA3AF';
  }
}

export function getXPToNextLevel(xp: number): number {
  const level = calculateLevel(xp);
  const nextThreshold = LEVEL_THRESHOLDS[level];
  if (nextThreshold === undefined) return 0;
  return nextThreshold - xp;
}
